import { Divider, Grid } from '@mui/material';
import Alert from '@mui/material/Alert';
import PaymentFormSection from 'components/payments/PaymentFormSection';
import FormFooter from 'components/shared/FormFooter';
import Spinner from 'components/shared/Spinner';
import SubscriptionDonationSection from 'components/subscriptions/SubscriptionDonationSection';
import SubscriptionPlanCard from 'components/subscriptions/SubscriptionPlanCard';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import PaymentService, { BasePaymentData, EnrollSubscriptionPaymentData } from 'services/api/PaymentService';
import { PaymentFormValues, PaymentItemsFormValues, SubscriptionPaymentFormValues } from 'store/types/FormValues';
import Payment from 'store/types/Payment';
import PaymentMethodType from 'store/enums/PaymentMethodType';
import { PaymentMethodFormHandle } from 'store/types/payments/PaymentMethodFormProps';
import Subscription from 'store/types/Subscription';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import { getDonationsPaymentData } from 'util/Membership';
import {
  defaultPaymentFormValues,
  donationsAmountSelected,
  getPaymentData,
  getSummaryTotalPrice,
  paymentFormInvalid,
  shouldValidatePaymentMethod,
} from 'util/Payment';
import SubscriptionService from 'services/api/SubscriptionService';
import Donation from 'store/types/Donation';
import useRequest from 'hooks/useRequest';
import { PaymentMethodsContext } from 'components/PaymentMethodsContextWrapper';
import { DEFAULT_ERROR_MESSAGE } from 'util/Form';
import { SiteConfigView } from 'store/types/SiteConfig';
import { ConfigContext } from 'components/ConfigGuard';

import commonStyles from 'styles/common.module.scss';

interface RenewSubscriptionPageViewProps {
  data: Subscription[];
  onPaymentCompleted: (newPayment: Payment) => void;
}

const getSubmitButtonDisabled = (paymentForm: PaymentFormValues, donations: PaymentItemsFormValues): boolean => {
  return !donationsAmountSelected(donations) || paymentFormInvalid(paymentForm);
};

const RenewSubscriptionPageView: React.FunctionComponent<RenewSubscriptionPageViewProps> = ({
  data,
  onPaymentCompleted,
}) => {
  const {
    modulesSettings: {
      payments: { invoices },
    },
  }: SiteConfigView = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();
  const { subscriptionId = '' } = useParams<{ subscriptionId: string }>();
  const { refetch: refetchPaymentMethods } = useContext(PaymentMethodsContext);
  const donationsRequest = useRequest<Donation[]>(SubscriptionService.getDonations);
  const [donations, setDonations] = useState<PaymentItemsFormValues>({});
  const [paymentForm, setPaymentForm] = useState<SubscriptionPaymentFormValues>({
    ...defaultPaymentFormValues,
    quantity: 0,
  });
  const paymentFormSectionRef = useRef<PaymentMethodFormHandle>(null);
  const [subscription, setSubscription] = useState<Subscription | undefined>(undefined);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const submitButtonDisabled = getSubmitButtonDisabled(paymentForm, donations);
  const { summaryList, quantity } = paymentForm;

  useEffect(() => {
    if (data && data.length && subscriptionId) {
      const foundSubscription: Subscription | undefined = data.find(({ id }) => id === subscriptionId);

      if (foundSubscription && foundSubscription.canRenew) {
        setSubscription(foundSubscription);
      }
    }
  }, [data, subscriptionId]);

  useEffect(() => {
    setPaymentForm((prevState) => ({
      ...prevState,
      totalPrice: getSummaryTotalPrice(summaryList),
    }));
  }, [summaryList]);

  useEffect(() => {
    setPaymentForm((prevState) => {
      const newQuantity = subscription && prevState.quantity === 0 ? subscription.quantity || 1 : prevState.quantity;

      return {
        ...prevState,
        quantity: newQuantity,
        summaryList: [],
      };
    });
  }, [donations, subscription]);

  const handleUsersAmountChange = useCallback((usersAmount) => {
    setPaymentForm((prevState) => ({
      ...prevState,
      quantity: usersAmount,
      summaryList: [],
    }));
  }, []);

  const handleDonationSelected = useCallback((newDonations: PaymentItemsFormValues) => {
    setDonations(newDonations);
  }, []);

  const handlePaymentChange = useCallback((formValues: Partial<PaymentFormValues>) => {
    setPaymentForm((prevForm) => ({ ...prevForm, ...formValues }));
  }, []);

  const renewSubscription = useCallback(
    (finalizedPaymentForm: SubscriptionPaymentFormValues) => {
      if (subscription) {
        const donationsData = getDonationsPaymentData(donations);
        const basePaymentData: BasePaymentData = getPaymentData(finalizedPaymentForm);

        const paymentData: EnrollSubscriptionPaymentData = {
          ...basePaymentData,
          poNumber: finalizedPaymentForm.poNumber,
          planId: subscriptionId,
          donations: donationsData,
          autoRenewal: finalizedPaymentForm.autoRenewal || false,
          quantity: finalizedPaymentForm.quantity || subscription.quantity || 1,
          renew: true,
        };

        PaymentService.makeSubscriptionPayment(paymentData)
          .then((newPayment: Payment) => {
            refetchPaymentMethods();
            enqueueSnackbar('Subscription successfully updated', { variant: 'success' });
            onPaymentCompleted(newPayment);
            setSubmitLoading(false);
          })
          .catch((errorMessage: string) => {
            setSubmitLoading(false);
            enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
          });

        return;
      }

      setSubmitLoading(false);
      enqueueSnackbar(DEFAULT_ERROR_MESSAGE, defaultSnackbarErrorProps);
    },
    [subscription, donations, enqueueSnackbar, subscriptionId, onPaymentCompleted, refetchPaymentMethods]
  );

  const handleSubmit = useCallback(async () => {
    const needsNewPaymentMethod =
      !paymentForm.paymentMethodId &&
      (paymentForm.paymentMethodType === PaymentMethodType.Card ||
        paymentForm.paymentMethodType === PaymentMethodType.Ach);

    if (needsNewPaymentMethod && !shouldValidatePaymentMethod(paymentForm)) {
      return;
    }

    setSubmitLoading(true);

    try {
      let paymentMethodValues: Partial<PaymentFormValues> | undefined;

      if (needsNewPaymentMethod) {
        if (!paymentFormSectionRef.current) {
          throw new Error('Payment form is not available.');
        }

        paymentMethodValues = await paymentFormSectionRef.current.finalizePaymentMethod();
      }

      const finalizedPaymentForm: SubscriptionPaymentFormValues = {
        ...paymentForm,
        ...paymentMethodValues,
      };

      renewSubscription(finalizedPaymentForm);
    } catch (error) {
      setSubmitLoading(false);

      enqueueSnackbar(
        error instanceof Error ? error.message : 'Payment could not be completed.',
        defaultSnackbarErrorProps
      );
    }
  }, [enqueueSnackbar, paymentForm, renewSubscription]);

  return subscription && subscription.id ? (
    <Spinner loading={submitLoading}>
      <form>
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps}>
            <SubscriptionPlanCard
              data={{
                ...subscription,
                price: 0,
                usersAmount: quantity,
                startDate: subscription.startDate ? moment(subscription.startDate) : undefined,
              }}
              selected={true}
              customSelectedLabel={'Your Subscription'}
              onUsersAmountChange={handleUsersAmountChange}
            />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <SubscriptionDonationSection {...donationsRequest} value={donations} onChange={handleDonationSelected} />
            <Divider className={commonStyles.gridItemDivider} />
          </Grid>
          <Grid {...defaultGridItemProps}>
            <PaymentFormSection
              ref={paymentFormSectionRef}
              form={paymentForm}
              onChange={handlePaymentChange}
              postponePaymentMethodVisible={true}
              autoRenewalVisible={true}
              paymentConfig={invoices}
            />
          </Grid>
        </Grid>
        <FormFooter
          onSubmit={handleSubmit}
          submitButtonName={'Complete Payment'}
          submitButtonDisabled={submitButtonDisabled}
        />
      </form>
    </Spinner>
  ) : (
    <Alert severity={'error'} className={commonStyles.alert}>
      {'Subscription not found'}
    </Alert>
  );
};
export default RenewSubscriptionPageView;
